"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Eye, EyeOff, PenSquare, Trash2, GripVertical, Plus, Search, X, Lock } from "lucide-react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { EditFieldModal } from "@/components/edit-field-modal"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { getLocalizedField } from "@/utils/language-utils"
import { useLanguage } from '@/context/language-context'
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import { useFeatureFlags } from '@/contexts/feature-flag-context'

// Import AlertDialog components
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface EditColumnsProps {
  isOpen: boolean
  onClose: () => void
  mode?: 'edit' | 'create'
  viewTitle?: string
  onViewTitleChange?: (title: string) => void
  isPublic?: boolean
  onIsPublicChange?: (isPublic: boolean) => void
  isFromManageViews?: boolean
  viewId?: string | null
}

// Helper for sortable item
function SortableItem({ id, children, disabled = false }: { id: string, children: React.ReactNode, disabled?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? '#f3f4f6' : undefined,
        cursor: disabled ? 'not-allowed' : 'grab',
      }}
      {...attributes}
      {...(!disabled ? listeners : {})}
    >
      {children}
    </div>
  )
}

function ColumnActions({ column, onEdit, onDelete, canDeleteCustomColumns, canEditCustomColumns, isVisible, onToggleVisibility }: {
  column: any,
  onEdit: () => void,
  onDelete: () => void,
  canDeleteCustomColumns: boolean,
  canEditCustomColumns: boolean,
  isVisible?: boolean,
  onToggleVisibility?: () => void,
}) {
  return (
    <div className="flex items-center gap-1">
      {onToggleVisibility && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleVisibility}
          className="p-1 h-auto w-auto"
        >
          {isVisible ? <Eye size={14} className="text-gray-400" /> : <EyeOff size={14} className="text-gray-400" />}
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={onEdit}
        className="p-1 h-auto w-auto"
        disabled={!canEditCustomColumns}
      >
        <PenSquare size={14} className="text-gray-400" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={column.isCustomField && canDeleteCustomColumns ? onDelete : undefined}
        disabled={!column.isCustomField || !canDeleteCustomColumns}
        className="p-1 h-auto w-auto"
      >
        <Trash2 size={14} className={column.isCustomField && canDeleteCustomColumns ? "text-gray-400" : "text-gray-400 opacity-65 cursor-not-allowed"} />
      </Button>
    </div>
  )
}

export function EditColumns({ isOpen, onClose, mode = 'edit', viewTitle = '', onViewTitleChange, isPublic = false, onIsPublicChange, isFromManageViews = false, viewId = null }: EditColumnsProps) {
  const { configuration, currentStage, deleteColumn, refreshConfiguration, currentView, stageFilters, globalFilters, state, setUnsavedChanges, updateVisibleColumns, viewsResponse } = useWorkflow()
  const { toast } = useToast()
  const { t } = useTranslation()
  const { deleteFieldConfiguration, saveViewState, saveViewSettings, fetchView } = useWorkflowApi()
  const { refreshViews } = useWorkflow()
  const { trackColumnAction, trackViewAction } = useWorkflowTracking()
  const [hiddenSearchTerm, setHiddenSearchTerm] = useState("")
  const [visibleSearchTerm, setVisibleSearchTerm] = useState("")
  const [isAddCustomColumnOpen, setIsAddCustomColumnOpen] = useState(false)
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null)
  const [editingColumn, setEditingColumn] = useState<any | null>(null)
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [stageFieldsOrder, setStageFieldsOrder] = useState<string[]>([])
  const [pendingVisibleFields, setPendingVisibleFields] = useState<string[]>([])
  const [isApplying, setIsApplying] = useState(false)
  const hasLoadedViewConfig = useRef(false)
  const { language } = useLanguage();
  const { isFeatureEnabled } = useFeatureFlags();

  // Feature flag to hide message column
  const isMessageColumnHidden = !isFeatureEnabled('messages');

  // Permission helpers
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode);
  const canDeleteCustomColumns = authorizedActions.includes("DeleteCustomColumns");
  const canEditCustomColumns = authorizedActions.includes("EditColumns");

  // Find the current stage configuration
  const stageConfig = configuration?.stageConfigurations.find(
    (s) => s.stageCode.toLowerCase() === currentStage?.toLowerCase()
  )

  // Get all fields and current stage field IDs
  const stageFieldIds = stageConfig?.fieldConfigurations.map(f => f.fieldConfigurationId) || [];
  
  // Filter out Message column if feature flag is disabled (memoized to prevent infinite loops)
  const allFields = useMemo(() => {
    const allFieldsRaw = configuration?.fieldsInUse || [];
    return isMessageColumnHidden 
      ? allFieldsRaw.filter(f => f.columnCode !== 'Message')
      : allFieldsRaw;
  }, [configuration?.fieldsInUse, isMessageColumnHidden]);

  // Helper to order fields with Message and History always first
  const orderFieldsWithFixedColumns = (fieldIds: string[]): string[] => {
    const messageFieldId = !isMessageColumnHidden ? allFields.find(f => f.columnCode === 'Message')?.fieldConfigurationId : undefined
    const historyFieldId = allFields.find(f => f.columnCode === 'History')?.fieldConfigurationId
    
    const otherFieldIds = fieldIds.filter(id => id !== messageFieldId && id !== historyFieldId)
    const orderedIds: string[] = []
    
    if (messageFieldId && fieldIds.includes(messageFieldId)) {
      orderedIds.push(messageFieldId)
    }
    if (historyFieldId && fieldIds.includes(historyFieldId)) {
      orderedIds.push(historyFieldId)
    }
    
    return [...orderedIds, ...otherFieldIds]
  };

  // Initialize pending changes when modal opens
  useEffect(() => {
    if (isOpen && stageConfig && !hasLoadedViewConfig.current) {
      hasLoadedViewConfig.current = true
      
      if (isFromManageViews && viewId) {
        // Load the specific view's configuration for the current stage
        const loadViewConfiguration = async () => {
          try {
            const viewData = await fetchView(viewId)
            
            // Find the configuration for the current stage
            const currentStageConfig = viewData.stageConfigurations?.find(
              (config: any) => config.stageConfigurationId === stageConfig.stageId
            )
            
            if (currentStageConfig) {
              // Get the field configuration IDs for this stage
              const visibleFieldIds = currentStageConfig.fieldConfigurationIds || []
              const orderedFieldIds = orderFieldsWithFixedColumns(visibleFieldIds)
              
              setPendingVisibleFields(visibleFieldIds)
              setStageFieldsOrder(orderedFieldIds)
            } else {
              // Fallback to current configuration if no stage config found
              const orderedFieldIds = orderFieldsWithFixedColumns(stageFieldIds)
              
              setPendingVisibleFields([...stageFieldIds])
              setStageFieldsOrder(orderedFieldIds)
            }
          } catch (error: any) {
            console.error('Failed to load view configuration:', error)
            
            // If it's a 404, show error and close modal
            if (error?.response?.status === 404) {
              toast({
                variant: "destructive",
                title: t('error') || "Error",
                description: t('viewNotFound') || "View not found. It may have been deleted.",
              })
              
              // Refresh views and close modal
              try {
                await refreshViews()
              } catch (refreshError) {
                console.error('Failed to refresh views:', refreshError)
              }
              
              onClose()
              return
            }
            
            // For other errors, fallback to current configuration
            const orderedFieldIds = orderFieldsWithFixedColumns(stageFieldIds)
            
            setPendingVisibleFields([...stageFieldIds])
            setStageFieldsOrder(orderedFieldIds)
          }
        }
        
        loadViewConfiguration()
      } else {
        // Use current stage configuration for regular edit columns or create new view
        const orderedFieldIds = orderFieldsWithFixedColumns(stageFieldIds)
        
        setPendingVisibleFields([...stageFieldIds])
        setStageFieldsOrder(orderedFieldIds)
      }
    }
  }, [isOpen, stageConfig, isFromManageViews, viewId, stageFieldIds])

  // Reset the ref when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasLoadedViewConfig.current = false
    }
  }, [isOpen])

  // Keep modal lists in sync if configuration updates while open
  useEffect(() => {
    if (!isOpen) return;
    const updatedStageConfig = configuration?.stageConfigurations.find(
      (s) => s.stageCode.toLowerCase() === currentStage?.toLowerCase()
    );
    if (!updatedStageConfig) return;
    const updatedVisibleIds = updatedStageConfig.fieldConfigurations.map(f => f.fieldConfigurationId);
    const orderedFieldIds = orderFieldsWithFixedColumns(updatedVisibleIds)
    
    setPendingVisibleFields([...updatedVisibleIds]);
    setStageFieldsOrder(orderedFieldIds);
  }, [isOpen, configuration?.stageConfigurations, currentStage, allFields, isMessageColumnHidden])

  // Stage-specific fields (visible fields)
  const stageFields = (stageConfig?.fieldConfigurations || [])
    .filter(
      (col) => {
        const localizedName = getLocalizedField(col, 'columnName', language);
        return typeof localizedName === "string" &&
          localizedName.toLowerCase().includes(visibleSearchTerm.toLowerCase());
      }
    );

  // Non-stage fields (hidden fields) - using the original logic
  const nonStageFields = allFields
    .filter(f => !pendingVisibleFields.includes(f.fieldConfigurationId))
    .filter(
      (col) => {
        const localizedName = getLocalizedField(col, 'columnName', language);
        return typeof localizedName === "string" &&
          localizedName.toLowerCase().includes(hiddenSearchTerm.toLowerCase());
      }
    )
    .sort((a, b) => {
      const aName = getLocalizedField(a, 'columnName', language) || '';
      const bName = getLocalizedField(b, 'columnName', language) || '';
      return aName.localeCompare(bName);
    });

  // Visible fields filtered and sorted, with Message and History columns always first
  const visibleFields = allFields
    .filter(f => pendingVisibleFields.includes(f.fieldConfigurationId))
    .filter(
      (col) => {
        const localizedName = getLocalizedField(col, 'columnName', language);
        return typeof localizedName === "string" &&
          localizedName.toLowerCase().includes(visibleSearchTerm.toLowerCase());
      }
    )
    .sort((a, b) => {
      // Message column always comes first (if not hidden)
      if (!isMessageColumnHidden) {
        if (a.columnCode === 'Message') return -1;
        if (b.columnCode === 'Message') return 1;
      }
      
      // History column always comes second (or first if Message is hidden)
      if (a.columnCode === 'History') return -1;
      if (b.columnCode === 'History') return 1;
      
      const aName = getLocalizedField(a, 'columnName', language) || '';
      const bName = getLocalizedField(b, 'columnName', language) || '';
      return aName.localeCompare(bName);
    });

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // DnD handler - only updates local state, not API
  const handleDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      // Find the field being dragged
      const draggedField = allFields.find(f => f.fieldConfigurationId === active.id)
      const targetField = allFields.find(f => f.fieldConfigurationId === over.id)
      
      // Don't allow moving History column (and Message if not hidden)
      if (draggedField?.columnCode === 'History') {
        return
      }
      if (!isMessageColumnHidden && draggedField?.columnCode === 'Message') {
        return
      }
      
      // Don't allow dropping on History column (and Message if not hidden)
      if (targetField?.columnCode === 'History') {
        return
      }
      if (!isMessageColumnHidden && targetField?.columnCode === 'Message') {
        return
      }
      
      const oldIndex = stageFieldsOrder.indexOf(active.id)
      const newIndex = stageFieldsOrder.indexOf(over.id)
      const newOrder = arrayMove(stageFieldsOrder, oldIndex, newIndex)
      setStageFieldsOrder(newOrder)
    }
  }

  // Toggle field visibility
  const handleToggleFieldVisibility = (fieldId: string, isVisible: boolean) => {
    const field = allFields.find(f => f.fieldConfigurationId === fieldId)
    const fieldName = field ? getLocalizedField(field, 'columnName', language) : 'Unknown Field'
    
    if (isVisible) {
      // Remove from visible fields (move to hidden)
      setPendingVisibleFields(prev => prev.filter(id => id !== fieldId))
      setStageFieldsOrder(prev => prev.filter(id => id !== fieldId))
      // trackColumnAction('hide', fieldName)
    } else {
      // Add to visible fields (move from hidden)
      setPendingVisibleFields(prev => [...prev, fieldId])
      
      // Special handling for Message and History columns - always add to the top
      if (!isMessageColumnHidden && field?.columnCode === 'Message') {
        setStageFieldsOrder(prev => [fieldId, ...prev])
      } else if (field?.columnCode === 'History') {
        // Insert History after Message if Message exists and is not hidden, otherwise at the top
        setStageFieldsOrder(prev => {
          if (!isMessageColumnHidden) {
            const messageFieldId = allFields.find(f => f.columnCode === 'Message')?.fieldConfigurationId
            const messageIndex = messageFieldId ? prev.indexOf(messageFieldId) : -1
            if (messageIndex >= 0) {
              return [...prev.slice(0, messageIndex + 1), fieldId, ...prev.slice(messageIndex + 1)]
            }
          }
          return [fieldId, ...prev]
        })
      } else {
        setStageFieldsOrder(prev => [...prev, fieldId])
      }
      // trackColumnAction('show', fieldName)
    }
  }

  // Helper to build visible view IDs from viewsResponse
  const buildVisibleViewIds = (): string[] => {
    if (!viewsResponse) return []
    
    const visibleViewIds: string[] = []
    
    // Add default view if visible
    if (viewsResponse.defaultView && viewsResponse.defaultView.isVisible !== false) {
      visibleViewIds.push(viewsResponse.defaultView.viewSettingsId)
    }
    
    // Add user views
    if (viewsResponse.userViews) {
      viewsResponse.userViews.forEach(view => {
        if (view.isVisible !== false) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    // Add shared views
    if (viewsResponse.sharedViews) {
      viewsResponse.sharedViews.forEach(view => {
        if (view.isVisible !== false) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    // Add public views
    if (viewsResponse.publicViews) {
      viewsResponse.publicViews.forEach(view => {
        if (view.isVisible !== false) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    return visibleViewIds
  }

  // Apply changes
  const handleApply = async (createMode: boolean) => {
    if (!stageConfig) return;
    
    setIsApplying(true);
    try {
      // Build stage configurations from current state for view-settings
      const stageConfigurations = configuration?.stageConfigurations.map(stage => {
        if (stage.stageCode.toLowerCase() === currentStage?.toLowerCase()) {
          // Ensure Message and History columns are first in the order
          const orderedFieldIds = orderFieldsWithFixedColumns(stageFieldsOrder)
          
          return {
            stageConfigurationId: stage.stageId,
            fieldConfigurationIds: orderedFieldIds
          }
        } else {
          return {
            stageConfigurationId: stage.stageId,
            fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId)
          }
        }
      }) || [];

      // Serialize stage filters with values as JSON strings
      const serializedStageFilters = stageFilters.map((filter: any) => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }));

      // Build visible view IDs
      const visibleViewIds = buildVisibleViewIds();

      // Get column widths from state or use empty object
      const columnWidthsToSave = state.columnWidths && Object.keys(state.columnWidths).length > 0
        ? JSON.stringify(state.columnWidths)
        : JSON.stringify({})

      // Call view-settings endpoint for manage views, view-state for edit columns
      const request = {
        id: isFromManageViews ? (viewId || undefined) : currentView?.viewSettingsId,
        title: isFromManageViews ? viewTitle : (currentView?.title),
        isPublic: isFromManageViews ? isPublic : (currentView?.isPublic ?? false),
        isDefault: isFromManageViews ? undefined : (currentView?.isDefault ?? false),
        stageConfigurations,
        stageFilters: isFromManageViews && !viewId ? [] : serializedStageFilters, // Send empty arrays for new views
        globalFilters: isFromManageViews && !viewId ? [] : globalFilters.map(filter => ({
          fieldConfigurationId: filter.fieldConfigurationId,
          filter: filter.filter,
          values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined)
        })), // Send empty arrays for new views
        visibleViewIds, // Include visible view IDs
        columnWidths: columnWidthsToSave, // Use column widths from state or currentView
        itemsPerPage: state.itemsPerPage, // Always include items per page
        isUserViewStateUpdate: !isFromManageViews, // Set to true for view-state updates
        updateFilters: createMode
      };

      if (isFromManageViews) {
        await saveViewSettings(request);
        
        // Refresh views list after creating/editing a view
        try {
          await refreshViews();
        } catch (error) {
          console.warn('Failed to refresh views:', error);
        }
        
        // Refresh configuration to update the workflow state, scoped to the relevant view
        await refreshConfiguration(true, viewId || null);
      } else {
        // For edit columns (not from manage views), use view-state endpoint
        await saveViewState(request);
        // Set unsaved changes flag to trigger confirmation dialog when switching views
        setUnsavedChanges(true);
        
        // Update visible columns immediately for UI responsiveness
        if (configuration) {
          const orderedFields = stageFieldsOrder
            .map(id => configuration.fieldsInUse.find(f => f.fieldConfigurationId === id))
            .filter((f): f is typeof configuration.fieldsInUse[0] => f !== undefined);
          updateVisibleColumns(orderedFields);
        }
        
        // Fetch full configuration from server (without viewId) to get updated column metadata
        // This ensures we have latest CustomValues and other field properties
        // skipFilterUpdate=true preserves current filters, and useEffect will handle data refresh
        await refreshConfiguration(true, null, true);
      }
      
      // Track the view save action
      const actionType = isFromManageViews ? (viewId ? 'edit' : 'create') : 'save';
      const viewName = isFromManageViews ? viewTitle : (currentView?.title || 'Custom View');
      trackViewAction(actionType, viewName);
      
      onClose();
      toast({
        variant: "success",
        title: t('success'),
        description: isFromManageViews ? t('viewSavedSuccessfully') : t('columnConfigurationUpdatedSuccessfully'),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('error'),
        description: error instanceof Error ? error.message : t('failedToSaveView'),
      });
    } finally {
      setIsApplying(false);
    }
  }

  // Cancel changes
  const handleCancel = () => {
    // Reset to original state
    if (stageConfig) {
      setPendingVisibleFields([...stageFieldIds])
      setStageFieldsOrder(stageConfig.fieldConfigurations.map(f => f.fieldConfigurationId))
    }
    onClose();
  }

  // Delete logic
  const handleDeleteClick = (fieldConfigId: string) => {
    setColumnToDelete(fieldConfigId)
  }
  
  const confirmDelete = async () => {
    if (columnToDelete) {
      setDeleting(true)
      try {
        const field = allFields.find(f => f.fieldConfigurationId === columnToDelete)
        const fieldName = field ? getLocalizedField(field, 'columnName', language) : 'Unknown Field'
        
        await deleteFieldConfiguration(columnToDelete)
        await refreshConfiguration(true)
        deleteColumn(columnToDelete)
        
        // Track the column deletion
        trackColumnAction('delete', fieldName)
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to delete column",
        })
      }
      setDeleting(false)
      setColumnToDelete(null)
    }
  }

  const isCreateMode = mode === 'create';
  const modalTitle = isCreateMode ? t('createView') : t('editColumns');
  const modalSubtitle = isCreateMode ? t('editColumnsSubtitle') : t('editColumnsSubtitle');

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] sm:w-[90vw] max-w-[900px] min-w-[320px] sm:min-w-[600px] h-[90vh] sm:h-[85vh] max-h-[800px] min-h-[400px] sm:min-h-[500px] flex flex-col resize overflow-auto" aria-describedby="edit-columns-description">
          <div id="edit-columns-description" className="sr-only">
            {modalTitle} - {t('manageColumnsDescription', { stage: currentStage || '' })}
          </div>
          <DialogHeader className="flex-shrink-0">
            <div className="text-left">
              <DialogTitle>{modalTitle}</DialogTitle>
              {(isCreateMode || isFromManageViews) && (
                <div className="mt-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('title')}
                    </label>
                    <Input
                      placeholder={t('nameYourCustomView')}
                      value={viewTitle}
                      onChange={(e) => onViewTitleChange?.(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('visibility')}
                    </label>
                    <div className="flex items-center space-x-3">
                      <Switch
                        checked={isPublic}
                        onCheckedChange={(checked) => onIsPublicChange?.(checked)}
                      />
                      <span className="text-sm text-gray-600">
                        {isPublic ? t('public') : t('private')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <p className="text-sm text-gray-600 mt-2">{modalSubtitle}</p>
            </div>
          </DialogHeader>
          
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 min-h-0">
            {/* Left Column - Hidden Fields */}
            <div className="flex flex-col min-h-0">
              <div className="mb-4 flex-shrink-0">
                <div className="flex items-center gap-2 mb-3">
                  <EyeOff size={14} className="text-gray-500" />
                  <h3 className="text-sm font-medium text-gray-900">{t('hiddenColumns')}</h3>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    placeholder={t('search')}
                    value={hiddenSearchTerm}
                    onChange={(e) => setHiddenSearchTerm(e.target.value)}
                    className="pl-10 focus:border-none focus:ring-0 focus-visible:ring-0 border-b border-gray-300"
                    style={{ boxShadow: 'none' }}
                  />
                  {hiddenSearchTerm && (
                    <X className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer" size={16} onClick={() => setHiddenSearchTerm('')} />
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border rounded-md max-h-[200px] lg:max-h-none">
                {nonStageFields.map((field) => (
                  <div key={field.fieldConfigurationId} className="flex items-center justify-between py-2.5 px-3 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span 
                        className="truncate text-sm flex items-center gap-1"
                        style={{ color: field.mappingSource ? '#9333ea' : (field.isCustomField ? '#2563eb' : undefined) }}
                      >
                        {field.isPrivate && (
                          <Lock size={12} className="text-gray-500 flex-shrink-0" />
                        )}
                        {getLocalizedField(field, 'columnName', language)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <ColumnActions
                        column={field}
                        onEdit={() => { setEditingColumn(field); setIsEditFieldModalOpen(true); }}
                        onDelete={() => handleDeleteClick(field.fieldConfigurationId)}
                        canDeleteCustomColumns={canDeleteCustomColumns}
                        canEditCustomColumns={canEditCustomColumns}
                        isVisible={false}
                        onToggleVisibility={() => handleToggleFieldVisibility(field.fieldConfigurationId, false)}
                      />
                    </div>
                  </div>
                ))}
                {nonStageFields.length === 0 && (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    No hidden columns
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Visible Fields */}
            <div className="flex flex-col min-h-0">
              <div className="mb-4 flex-shrink-0">
                <div className="flex items-center gap-2 mb-3">
                  <Eye size={14} className="text-gray-500" />
                  <h3 className="text-sm font-medium text-gray-900">{t('visibleColumns')}</h3>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    placeholder={t('search')}
                    value={visibleSearchTerm}
                    onChange={(e) => setVisibleSearchTerm(e.target.value)}
                    className="pl-10 focus:border-none focus:ring-0 focus-visible:ring-0 border-b border-gray-300"
                    style={{ boxShadow: 'none' }}
                  />
                  {visibleSearchTerm && (
                    <X className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer" size={16} onClick={() => setVisibleSearchTerm('')} />
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border rounded-md max-h-[200px] lg:max-h-none">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={stageFieldsOrder} strategy={verticalListSortingStrategy}>
                    {stageFieldsOrder.map((id) => {
                      const field = visibleFields.find(f => f.fieldConfigurationId === id)
                      if (!field) return null
                      const isFixedColumn = field.columnCode === 'History' || (!isMessageColumnHidden && field.columnCode === 'Message')
                      return (
                        <SortableItem key={field.fieldConfigurationId} id={field.fieldConfigurationId} disabled={isFixedColumn}>
                          <div className="flex items-center justify-between py-2.5 px-3 border-b border-gray-200 last:border-b-0">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {isFixedColumn ? (
                                <div className="w-3.5 h-3.5" /> // Empty space to align with other items
                              ) : (
                                <GripVertical size={14} className="text-gray-400" />
                              )}
                              <span 
                                className="truncate text-sm flex items-center gap-1"
                                style={{ color: field.mappingSource ? '#9333ea' : (field.isCustomField ? '#2563eb' : undefined) }}
                              >
                                {field.isPrivate && (
                                  <Lock size={12} className="text-gray-500 flex-shrink-0" />
                                )}
                                {getLocalizedField(field, 'columnName', language)}
                                {isFixedColumn && <span className="text-xs text-gray-500 ml-2">({t('fixedPosition')})</span>}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <ColumnActions
                                column={field}
                                onEdit={() => { setEditingColumn(field); setIsEditFieldModalOpen(true); }}
                                onDelete={() => handleDeleteClick(field.fieldConfigurationId)}
                                canDeleteCustomColumns={canDeleteCustomColumns}
                                canEditCustomColumns={canEditCustomColumns}
                                isVisible={true}
                                onToggleVisibility={() => handleToggleFieldVisibility(field.fieldConfigurationId, true)}
                              />
                            </div>
                          </div>
                        </SortableItem>
                      )
                    })}
                  </SortableContext>
                </DndContext>
                {visibleFields.length === 0 && (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    No visible columns
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer with buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mt-4 pt-4 border-t flex-shrink-0">
            {!isCreateMode && !isFromManageViews && (
              <Button
                variant="ghost"
                className="text-blue-600 flex items-center gap-1 w-full sm:w-auto"
                onClick={() => setIsAddCustomColumnOpen(true)}
                disabled={!canEditCustomColumns}
              >
                <Plus size={16} />
                {t('customColumn')}
              </Button>
            )}
            <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
              <Button variant="elliptical-outline" onClick={handleCancel} disabled={isApplying} className="flex-1 sm:flex-none">
                {t('cancel')}
              </Button>
              <Button variant="elliptical" onClick={() => handleApply(isCreateMode)} disabled={isApplying} className="flex-1 sm:flex-none">
                {isApplying ? t('processing') : (isCreateMode ? t('saveView') : t('save'))}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {isAddCustomColumnOpen && (
        <EditFieldModal
          isOpen={isAddCustomColumnOpen}
          onClose={() => setIsAddCustomColumnOpen(false)}
          addMode={true}
          canEditCustomColumns={canEditCustomColumns}
        />
      )}
      
      {columnToDelete && (
        <AlertDialog open={!!columnToDelete} onOpenChange={() => setColumnToDelete(null)}>
          <AlertDialogContent aria-describedby="delete-column-description">
            <div id="delete-column-description" className="sr-only">
              Confirmation dialog to delete a column permanently
            </div>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
              <AlertDialogDescription>This action will delete the column and cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel variant="elliptical-outline">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} variant="elliptical" disabled={deleting}>
                {deleting ? t('processing') : t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      
      {isEditFieldModalOpen && editingColumn && (
        <EditFieldModal
          isOpen={isEditFieldModalOpen}
          onClose={() => { setIsEditFieldModalOpen(false); setEditingColumn(null); }}
          fieldConfiguration={editingColumn}
          canEditCustomColumns={canEditCustomColumns}
        />
      )}
    </>
  )
}
